import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { BellIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { Link } from 'react-router';
import type { Notice } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { useNoticeHeader } from '@/api/hanly-v2/notices/notices';
import { formatNoticeDate, MANUAL_URL, NoticeMarks } from './notice-display';

// ヘッダーは画面遷移では作り直されないため、利用中に公開されたお知らせも出るよう定期的に取り直す。
// 画面に戻ったときも、前回から間隔が空いていれば取り直す(共通設定ではフォーカス時に取り直さない)
const REFETCH_INTERVAL = 5 * 60 * 1000;

interface NoticeMenuItemProps {
  notice: Notice;
  onClick: () => void;
}

const NoticeMenuItem = ({ notice, onClick }: NoticeMenuItemProps) => {
  const content = (
    <>
      <span className="flex-none text-neutral-500">{formatNoticeDate(notice.published_at)}</span>
      <span className={clsx('flex-1 min-w-0', (notice.is_new || notice.is_important) && 'font-bold')}>
        {notice.title}
      </span>
      <NoticeMarks notice={notice} />
    </>
  );
  const className = 'flex items-baseline gap-3 px-4 py-2.5 text-sm text-zinc-800';
  return (
    <li className="border-b border-neutral-100">
      {notice.url ? (
        <a
          href={notice.url}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(className, 'hover:bg-neutral-100')}
          onClick={onClick}
        >
          {content}
        </a>
      ) : (
        <div className={className}>{content}</div>
      )}
    </li>
  );
};

/** ヘッダー上段のお知らせ。NEW の期間中のお知らせがあれば NEW、その中に重要なものがあれば「重要」を出す */
export const HeaderNoticeMenu = () => {
  const { data } = useNoticeHeader({
    query: { staleTime: REFETCH_INTERVAL, refetchInterval: REFETCH_INTERVAL, refetchOnWindowFocus: true },
  });

  return (
    <Popover className="flex items-center">
      {/* 一覧画面などがヘッダーの高さ(77px)を前提にしているため、上段の行の高さを変えないよう -my-0.5 で相殺する */}
      <PopoverButton className="-my-0.5 flex h-5 items-center gap-1 px-1.5 text-white cursor-pointer hover:bg-white/20 focus:outline-none data-[focus]:bg-white/20">
        <BellIcon className="w-3.5" aria-hidden="true" />
        <span>お知らせ</span>
        {data?.has_important ? (
          <span className="bg-red-600 px-1 text-[11px] font-bold leading-4">重要</span>
        ) : (
          data?.has_new && <span className="bg-sky-700 px-1 text-[11px] font-bold leading-4">NEW</span>
        )}
      </PopoverButton>
      <PopoverPanel
        anchor={{ to: 'bottom end', gap: 4 }}
        className="z-50 w-100 max-w-[calc(100vw-16px)] bg-white border border-neutral-200 shadow-[2px_2px_0px_0px_black]"
      >
        {({ close }) => (
          <>
            {data && data.items.length > 0 ? (
              <ul>
                {data.items.map((notice) => (
                  <NoticeMenuItem key={notice.id} notice={notice} onClick={() => close()} />
                ))}
              </ul>
            ) : (
              <p className="px-4 py-6 text-center text-sm text-neutral-500">お知らせはありません</p>
            )}
            <div className="flex justify-between px-4 py-2.5 text-sm">
              <Link to="/notices" className="text-sky-700 hover:underline" onClick={() => close()}>
                すべてのお知らせ
              </Link>
              <a
                href={MANUAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-700 hover:underline"
                onClick={() => close()}
              >
                マニュアル
              </a>
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
};
